import { Box, Skeleton, Typography } from "@mui/material";
import React, { useCallback } from "react";

import EllipsisTooltipText from "../ellipsis-tooltip-text";

interface IKpiCard {
	icon: any;
	iconBackgroundColor: string;
	iconColor: string;
	kpiDataCount: number | string;
	title: string;
	subTitle: string;
	loading: string;
	// A card that filters the page below it is a toggle and passes its state;
	// one that opens something (a drawer, a dialog) is not, and leaves this
	// off rather than pinning aria-pressed to false on a plain button.
	isToggled?: boolean;
	handleClick: (actionType: string, data: any) => void;
	onClickActionType?: string;
	kpiData: any;
	description?: string;
}

export default function KpiCard({
	icon,
	iconBackgroundColor,
	iconColor,
	kpiDataCount,
	title,
	subTitle,
	loading,
	isToggled,
	handleClick,
	onClickActionType,
	kpiData,
	description,
}: IKpiCard) {
	// A card only acts when it has an action type; that is also what decides
	// whether it is a control. An interactive one gets the button role, a tab
	// stop and Enter/Space, because until then the whole card was a plain <Box
	// onClick> that no keyboard could reach. A non-interactive one gets none of
	// those, so a page of read-only KPIs does not grow a row of empty tab stops.
	const isInteractive = loading !== "pending" && Boolean(onClickActionType);
	const fire = useCallback(() => {
		if (isInteractive && onClickActionType) {
			handleClick(onClickActionType, kpiData);
		}
	}, [isInteractive, onClickActionType, handleClick, kpiData]);
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key !== "Enter" && event.key !== " ") return;
			// Space scrolls the page otherwise, and Enter would submit a form
			event.preventDefault();
			fire();
		},
		[fire]
	);
	return (
		<Box
			role={isInteractive ? "button" : undefined}
			tabIndex={isInteractive ? 0 : undefined}
			aria-pressed={
				isInteractive && isToggled !== undefined ? isToggled : undefined
			}
			onKeyDown={isInteractive ? handleKeyDown : undefined}
			sx={{
				// Full-width stacked cards on phones; fixed 302px tiles from 600px up.
				width: { xs: "100%", sm: "302px" },
				minWidth: { xs: 0, sm: "302px" },
				height: "55px",
				boxShadow: isToggled
					? null
					: "0px 3px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)",
				borderRadius: "10px",
				padding: "8px",
				// a read-only KPI must not offer a hand cursor it cannot honour
				cursor: isInteractive ? "pointer" : "default",
				display: "flex",
				flexDirection: "row",
				alignItems: "center",
				backgroundColor: isToggled ? "rgba(0, 0, 0, 0.04)" : null,
				marginRight: { xs: 0, sm: 2 },
				marginBottom: 1,
				outline: isToggled ? "1px solid #8A99B8" : null,
				"&:focus-visible": {
					outline: "2px solid #2F4D8B",
					outlineOffset: "2px",
				},
			}}
			onClick={fire}
		>
			{loading === "pending" ? (
				<Box
					mt={1}
					mb={2}
					sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
				>
					<Skeleton
						variant='rounded'
						width={32}
						height={32}
						sx={{
							borderRadius: "15px",
							marginRight: 2,
						}}
					/>
					<Skeleton animation='wave' width={80} height={40} />
				</Box>
			) : (
				<>
					<Box
						style={{
							backgroundColor: iconBackgroundColor,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: 40,
							height: 40,
							borderRadius: "50%",
							color: iconColor,
						}}
					>
						{icon}
					</Box>

					{/* the count is sized like a heading, but it is a figure — a
					    heading element here puts every KPI on the page into a screen
					    reader's heading list */}
					<Typography
						component='span'
						variant='h5'
						pl={kpiDataCount.toString().length > 2 ? 0.2 : 0}
						p={kpiDataCount.toString().length < 3 ? 1 : 0}
						fontWeight={"bold"}
					>
						{kpiDataCount}
					</Typography>
					<Box pl={1} sx={{ minWidth: 0, flex: 1 }}>
						<EllipsisTooltipText
							text={title}
							sx={{ fontWeight: "450", fontSize: "0.875rem" }}
						/>
						<EllipsisTooltipText
							text={subTitle}
							sx={{
								fontSize: "14px",
								fontWeight: "400",
								fontStyle: "italic",
								color: "gray",
							}}
						/>
					</Box>
				</>
			)}
		</Box>
	);
}
